/**
 * LegalDocumentsPage
 *
 * Static public page listing all six legal documents. Each row navigates to
 * the corresponding policy page. Mirrors Flutter's `LegalDocumentsScreen`
 * (lib/presentation/screens/legal/legal_documents_screen.dart:107-180).
 *
 * Accessible without authentication. Uses the project's standard
 * `FlexibleHeaderList` shell (matching `UserSettingsPage`) for the
 * top-bar/scrolling layout, so future parallax / sticky-header polish is
 * a one-line change. The flexible space is intentionally omitted for now —
 * the non-sticky behaviour is the current target; a sticky/parallax pass is
 * a follow-up task.
 */
import { useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { LegalDocumentItemType } from '@/domain/enums/legalDocumentItemType';
import type { LegalDocumentItemEntity } from '@/domain/entities/legal/legalDocumentItemEntity';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { Navigator } from '@/presentation/routing/Navigator';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { ListItemTile } from '@/presentation/components/common/ListItemTile';
import { FlexibleHeaderList } from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderList';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { useLegalDocumentsItems } from './hooks/useLegalDocumentsItems';

export const LegalDocumentsPage = () => {
    const { theme } = useTheme();
    const responsive = useResponsiveDesign();
    const { l10n } = useLocale();
    const items = useLegalDocumentsItems();
    const location = useLocation();

    // Canonical screen-key wiring (mirrors `UserSettingsPage`): ties this
    // page instance to its `KeepAliveOutlet` cache entry so the back
    // navigation from this page restores the underlying cached page rather
    // than force-remounting it.
    const { currentScreenKeyRef } = useKeepAlive();
    const screenKeyRef = useRef<string>(
        currentScreenKeyRef.current || generateScreenKey(location.pathname)
    );
    const screenKey = screenKeyRef.current;

    useBasePage({ screenKey, onInit: () => { } });

    const onItemTap = useCallback((type: LegalDocumentItemType) => {
        switch (type) {
            case LegalDocumentItemType.terms:
                Navigator.navigateToTermsOfUse();
                break;
            case LegalDocumentItemType.coinsPolicy:
                Navigator.navigateToCoinsPolicy();
                break;
            case LegalDocumentItemType.rewardsPolicy:
                Navigator.navigateToRewardsPolicy();
                break;
            case LegalDocumentItemType.exclusiveSubscriptionsPolicy:
                Navigator.navigateToExclusiveSubscriptionsPolicy();
                break;
            case LegalDocumentItemType.premiumSubscriptionsPolicy:
                Navigator.navigateToPremiumSubscriptionsPolicy();
                break;
            case LegalDocumentItemType.privacyPolicy:
                Navigator.navigateToPrivacyPolicy();
                break;
            case LegalDocumentItemType.communityGuidelines:
                Navigator.navigateToCommunityGuidelines();
                break;
        }
    }, []);

    const renderLegalItem = useCallback(
        (item: LegalDocumentItemEntity) => (
            <div style={{ padding: `0 ${responsive.spacing.base}px` }}>
                <ListItemTile
                    title={item.title}
                    description={item.description}
                    iconPath={item.iconPath}
                    onClick={() => onItemTap(item.type)}
                    showChevron
                    qa={{ id: `legal-doc-${item.type}` }}
                />
            </div>
        ),
        [responsive.spacing.base, onItemTap]
    );

    const getItemKey = useCallback((item: LegalDocumentItemEntity) => item.type, []);

    return (
        <FlexibleHeaderList
            qa={{ id: 'legal-documents', status: 'ready' }}
            listQa={{ id: 'legal-documents-list' }}
            items={items}
            renderItem={renderLegalItem}
            getItemKey={getItemKey}
            hasMore={false}
            onLoadMore={() => { }}
            backgroundColor={theme.background}
            appBarOptions={{
                title: l10n.legal.title,
                // Deep-link guard: the legal-documents list page has no
                // logical parent inside the `/legal` namespace, so when there
                // is no in-app history fall back to the feed (app home).
                onLeadingTap: () => Navigator.goBackOr(() => Navigator.navigateToFeed()),
                backgroundColor: theme.background,
            }}
        />
    );
};
